<template>
  <div class="chips-wrapper overflow-gutters-row">
    <Button
      v-if="canScrollLeft && !hideArrows"
      class="chevron chevron-left"
      variant="button-icon"
      size="small"
      theme="dark"
      icon="chevron-large-left-light-small"
      aria-label="Scroll left"
      @click="scrollLeft"
    />
    <div ref="chipsContainer" class="chips-container" @scroll="updateScrollState">
      <slot></slot>
    </div>
    <Button
      v-if="canScrollRight && !hideArrows"
      class="chevron chevron-right"
      variant="button-icon"
      size="small"
      theme="dark"
      icon="chevron-large-right-light-small"
      aria-label="Scroll right"
      @click="scrollRight"
    />
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, onBeforeUnmount } from 'vue';
import { useDebounceFn } from '@vueuse/core';
import Button from '@atomic-ui/button/Button';

defineProps({
  hideArrows: {
    type: Boolean,
    default: false,
  },
});

const chipsContainer = ref<HTMLElement | null>(null);
const canScrollLeft = ref(false);
const canScrollRight = ref(false);

const SCROLL_DISTANCE = 250;

const updateScrollState = () => {
  if (chipsContainer.value) {
    const { scrollLeft: sLeft, scrollWidth, clientWidth } = chipsContainer.value;
    canScrollLeft.value = sLeft > 0;
    canScrollRight.value = scrollWidth > clientWidth + sLeft + 1; // +1 for subpixel rounding issues
  }
};

const updateScrollStateDebounced = useDebounceFn(updateScrollState, 100);

const scrollLeft = () => {
  if (chipsContainer.value) {
    chipsContainer.value.scrollBy({ left: -SCROLL_DISTANCE, behavior: 'smooth' });
  }
};

const scrollRight = () => {
  if (chipsContainer.value) {
    chipsContainer.value.scrollBy({ left: SCROLL_DISTANCE, behavior: 'smooth' });
  }
};

onMounted(() => {
  updateScrollState();
  window.addEventListener('resize', updateScrollStateDebounced);
});

onBeforeUnmount(() => {
  window.removeEventListener('resize', updateScrollStateDebounced);
});

// Expose for testing
defineExpose({
  updateScrollState,
});
</script>

<style lang="scss" scoped>
@import '~settingSCSS';
@import '~/assets/scss/utils/overflow-gutters';

.chips-wrapper {
  display: flex;
  align-items: center;
  gap: rem-calc(8);
  position: relative;
}

.chips-container {
  flex: 1;
  display: flex;
  align-items: center;
  overflow-x: auto;
  overflow-y: hidden;
  scroll-behavior: smooth;
  white-space: nowrap;
  -ms-overflow-style: none; /* IE and Edge */
  scrollbar-width: none; /* Firefox */
  gap: rem-calc(8);
  min-width: 0;
  min-height: 36px;

  &::-webkit-scrollbar {
    display: none; /* Chrome, Safari and Opera */
  }

  @include breakpoint(medium down) {
    &::before,
    &::after {
      content: '';
      flex: 0 0 calc(var(--gap) - #{rem-calc(8)});
    }
  }
}

.chevron {
  flex-shrink: 0;
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  z-index: 2;
  display: none;

  @include breakpoint(large up) {
    display: inline-flex;
  }

  &.chevron-left {
    left: 0px;
  }

  &.chevron-right {
    right: 0px;
  }
}
</style>
